import { useChartStore } from '@/store/chartStore';
import type { SellEvent, SellRule } from '@/indicators/williamOneilSell';
import { cancelSellTooltipClear, scheduleSellTooltipClear } from './sellTooltipState';

const RULE_LABEL: Record<SellRule, string> = {
  ma50Break:             '50-day MA Break',
  ma200Break:            '200-day MA Break',
  climaxTop:             'Climax Top',
  exhaustionGap:         'Exhaustion Gap',
  distributionDay:       'Distribution Day',
  distributionThreshold: 'Distribution Threshold',
};

const RULE_COLOR: Record<SellRule, string> = {
  ma50Break:             '#F97316',
  ma200Break:            '#DC2626',
  climaxTop:             '#EF4444',
  exhaustionGap:         '#B91C1C',
  distributionDay:       '#FCA5A5',
  distributionThreshold: '#7C3AED',
};

function SellEventCard({ ev }: { ev: SellEvent }) {
  const color = RULE_COLOR[ev.rule];
  const label = RULE_LABEL[ev.rule];
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color }}>{label}</span>
      </div>
      <p className="text-xs font-medium text-white leading-tight">{ev.label}</p>
    </div>
  );
}

export function WilliamOneilSellTooltip() {
  const hoveredEvents = useChartStore((s) => s.hoveredSellEvents);
  const hoveredBar    = useChartStore((s) => s.hoveredSellBar);
  const hoveredDate   = useChartStore((s) => s.hoveredSellDate);
  const pinnedEvents  = useChartStore((s) => s.pinnedSellEvents);
  const pinnedBar     = useChartStore((s) => s.pinnedSellBar);
  const pinnedDate    = useChartStore((s) => s.pinnedSellDate);
  const setPinnedSellEvents = useChartStore((s) => s.setPinnedSellEvents);

  // Pinned takes priority over hovered
  const events = pinnedEvents ?? hoveredEvents;
  const pos    = pinnedBar ?? hoveredBar;
  const date   = pinnedEvents ? pinnedDate : hoveredDate;
  const pinned = pinnedEvents !== null;

  if (!events || !pos || events.length === 0) return null;

  const TOOLTIP_W = 220;
  const left   = Math.max(8, pos.x - TOOLTIP_W / 2);
  const bottom = `calc(100% - ${pos.y - 18}px)`;

  return (
    <div
      data-chart-tooltip
      className="absolute z-dropdown pointer-events-auto select-text"
      style={{ left, bottom, width: TOOLTIP_W, maxWidth: 'calc(100% - 16px)' }}
      onMouseEnter={cancelSellTooltipClear}
      onMouseLeave={() => { if (!pinned) scheduleSellTooltipClear(); }}
      onMouseMove={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-2.5 text-left">
        {pinned && (
          <button
            className="absolute top-1.5 right-1.5 text-gray-500 hover:text-white leading-none text-base"
            onClick={() => setPinnedSellEvents(null, null)}
            aria-label="Close"
          >
            ×
          </button>
        )}
        {date && (
          <p className="text-[10px] text-gray-400 mb-1.5">{date}</p>
        )}
        {events.map((ev, idx) => (
          <SellEventCard key={`${ev.rule}-${ev.index}-${idx}`} ev={ev} />
        ))}
      </div>
    </div>
  );
}
